import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Default theme configuration
const defaultTheme = {
  // Brand Colors
  primaryColor: '#E91E63',
  secondaryColor: '#1e3a5f',
  accentColor: '#10b981',
  
  // Background Colors
  backgroundColor: '#FDF8F4',
  cardBackground: '#ffffff',
  headerBackground: '#1e3a5f',
  footerBackground: '#1e3a5f',
  
  // Text Colors
  textColor: '#232323',
  textLight: '#6b7280',
  headerText: '#ffffff',
  footerText: '#ffffff',
  linkColor: '#E91E63',
  
  // Button Colors
  buttonPrimary: '#E91E63',
  buttonPrimaryText: '#ffffff',
  buttonSecondary: '#1e3a5f',
  buttonSecondaryText: '#ffffff',
  buttonDanger: '#ef4444',
  buttonDangerText: '#ffffff',
  
  // Input/Form Colors
  inputBackground: '#ffffff',
  inputBorder: '#d1d5db',
  inputFocus: '#E91E63',
  
  // Typography
  fontFamily: 'Inter',
  headingFont: 'Poppins',
  
  // Border Radius
  borderRadius: 'medium', // small, medium, large, full
  
  // Preset Theme
  preset: 'custom' // light, dark, pink, blue, custom
};

const ThemeContext = createContext({
  theme: defaultTheme,
  loading: true,
  updateTheme: () => {}
});

export const useTheme = () => useContext(ThemeContext);

// Convert hex to HSL for CSS variables
const hexToHSL = (hex) => {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  
  r /= 255;
  g /= 255;
  b /= 255;
  
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// Get border radius value
const getBorderRadius = (size) => {
  switch (size) {
    case 'small': return '4px';
    case 'medium': return '8px';
    case 'large': return '16px';
    case 'full': return '9999px';
    default: return '8px';
  }
};

// Apply theme to document
const applyThemeToDocument = (theme) => {
  const root = document.documentElement;
  
  // Brand colors
  root.style.setProperty('--color-primary', theme.primaryColor);
  root.style.setProperty('--color-secondary', theme.secondaryColor);
  root.style.setProperty('--color-accent', theme.accentColor);
  
  // Background colors
  root.style.setProperty('--color-background', theme.backgroundColor);
  root.style.setProperty('--color-card-bg', theme.cardBackground);
  root.style.setProperty('--color-header-bg', theme.headerBackground);
  root.style.setProperty('--color-footer-bg', theme.footerBackground);
  
  // Text colors
  root.style.setProperty('--color-text', theme.textColor);
  root.style.setProperty('--color-text-light', theme.textLight);
  root.style.setProperty('--color-header-text', theme.headerText);
  root.style.setProperty('--color-footer-text', theme.footerText);
  root.style.setProperty('--color-link', theme.linkColor);
  
  // Button colors
  root.style.setProperty('--color-btn-primary', theme.buttonPrimary);
  root.style.setProperty('--color-btn-primary-text', theme.buttonPrimaryText);
  root.style.setProperty('--color-btn-secondary', theme.buttonSecondary);
  root.style.setProperty('--color-btn-secondary-text', theme.buttonSecondaryText);
  root.style.setProperty('--color-btn-danger', theme.buttonDanger);
  root.style.setProperty('--color-btn-danger-text', theme.buttonDangerText);
  
  // Input colors
  root.style.setProperty('--color-input-bg', theme.inputBackground);
  root.style.setProperty('--color-input-border', theme.inputBorder);
  root.style.setProperty('--color-input-focus', theme.inputFocus);
  
  // Border radius
  root.style.setProperty('--border-radius', getBorderRadius(theme.borderRadius));
  root.style.setProperty('--border-radius-sm', theme.borderRadius === 'full' ? '9999px' : `${parseInt(getBorderRadius(theme.borderRadius)) / 2}px`);
  root.style.setProperty('--border-radius-lg', theme.borderRadius === 'full' ? '9999px' : `${parseInt(getBorderRadius(theme.borderRadius)) * 2}px`);
  
  // Fonts
  root.style.setProperty('--font-family', theme.fontFamily || 'Inter');
  root.style.setProperty('--heading-font', theme.headingFont || 'Poppins');
  
  // Apply to body
  document.body.style.backgroundColor = theme.backgroundColor;
  document.body.style.color = theme.textColor;
  document.body.style.fontFamily = `${theme.fontFamily || 'Inter'}, -apple-system, BlinkMacSystemFont, sans-serif`;
  document.body.style.fontWeight = theme.fontWeight || '400';
  
  // Apply font size based on theme setting
  const fontSizeMap = {
    'xs': '12px',
    'sm': '14px', 
    'base': '16px',
    'lg': '18px',
    'xl': '20px'
  };
  document.body.style.fontSize = fontSizeMap[theme.fontSize] || '16px';
  
  // Set CSS custom properties for fonts
  root.style.setProperty('--font-family', `${theme.fontFamily || 'Inter'}, -apple-system, BlinkMacSystemFont, sans-serif`);
  root.style.setProperty('--font-heading', `${theme.headingFont || 'Poppins'}, -apple-system, BlinkMacSystemFont, sans-serif`);
  root.style.setProperty('--font-weight', theme.fontWeight || '400');
  root.style.setProperty('--font-size-body', fontSizeMap[theme.fontSize] || '16px');
  
  // Heading font sizes based on theme
  const headingSizeMap = {
    'xs': { h1: '24px', h2: '20px', h3: '18px' },
    'sm': { h1: '28px', h2: '24px', h3: '20px' },
    'base': { h1: '32px', h2: '28px', h3: '24px' },
    'lg': { h1: '36px', h2: '32px', h3: '28px' },
    'xl': { h1: '42px', h2: '36px', h3: '32px' }
  };
  const headingSizes = headingSizeMap[theme.fontSize] || headingSizeMap['base'];
  root.style.setProperty('--font-size-h1', headingSizes.h1);
  root.style.setProperty('--font-size-h2', headingSizes.h2);
  root.style.setProperty('--font-size-h3', headingSizes.h3);
  
  // Shadcn/Tailwind CSS variables (HSL format)
  root.style.setProperty('--primary', hexToHSL(theme.primaryColor));
  root.style.setProperty('--primary-foreground', hexToHSL(theme.buttonPrimaryText));
  root.style.setProperty('--secondary', hexToHSL(theme.secondaryColor));
  root.style.setProperty('--secondary-foreground', hexToHSL(theme.buttonSecondaryText));
  root.style.setProperty('--accent', hexToHSL(theme.accentColor));
  root.style.setProperty('--accent-foreground', hexToHSL(theme.textColor));
  root.style.setProperty('--background', hexToHSL(theme.backgroundColor));
  root.style.setProperty('--foreground', hexToHSL(theme.textColor));
  root.style.setProperty('--card', hexToHSL(theme.cardBackground));
  root.style.setProperty('--card-foreground', hexToHSL(theme.textColor));
  root.style.setProperty('--muted', hexToHSL(theme.inputBackground));
  root.style.setProperty('--muted-foreground', hexToHSL(theme.textLight));
  root.style.setProperty('--destructive', hexToHSL(theme.buttonDanger));
  root.style.setProperty('--destructive-foreground', hexToHSL(theme.buttonDangerText));
  root.style.setProperty('--border', hexToHSL(theme.inputBorder));
  root.style.setProperty('--input', hexToHSL(theme.inputBorder));
  root.style.setProperty('--ring', hexToHSL(theme.inputFocus));
  
  // Legacy support
  root.style.setProperty('--mint-500', theme.primaryColor);
  root.style.setProperty('--navy-500', theme.secondaryColor);
  root.style.setProperty('--coral-500', theme.accentColor);
};

// Preset themes
export const presetThemes = {
  light: {
    primaryColor: '#E91E63',
    secondaryColor: '#1e3a5f',
    accentColor: '#10b981',
    backgroundColor: '#FDF8F4',
    cardBackground: '#ffffff',
    headerBackground: '#1e3a5f',
    footerBackground: '#1e3a5f',
    textColor: '#232323',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#ffffff',
    linkColor: '#E91E63',
    buttonPrimary: '#E91E63',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#1e3a5f',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#d1d5db',
    inputFocus: '#E91E63',
    borderRadius: 'medium',
    preset: 'light'
  },
  dark: {
    primaryColor: '#f472b6',
    secondaryColor: '#0f172a',
    accentColor: '#34d399',
    backgroundColor: '#0f172a',
    cardBackground: '#1e293b',
    headerBackground: '#0f172a',
    footerBackground: '#0f172a',
    textColor: '#f1f5f9',
    textLight: '#94a3b8',
    headerText: '#f1f5f9',
    footerText: '#94a3b8',
    linkColor: '#f472b6',
    buttonPrimary: '#f472b6',
    buttonPrimaryText: '#0f172a',
    buttonSecondary: '#334155',
    buttonSecondaryText: '#f1f5f9',
    buttonDanger: '#f87171',
    buttonDangerText: '#0f172a',
    inputBackground: '#1e293b',
    inputBorder: '#334155',
    inputFocus: '#f472b6',
    borderRadius: 'medium',
    preset: 'dark'
  },
  pink: {
    primaryColor: '#ec4899',
    secondaryColor: '#831843',
    accentColor: '#f472b6',
    backgroundColor: '#fdf2f8',
    cardBackground: '#ffffff',
    headerBackground: '#831843',
    footerBackground: '#831843',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#fce7f3',
    linkColor: '#ec4899',
    buttonPrimary: '#ec4899',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#831843',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#fbcfe8',
    inputFocus: '#ec4899',
    borderRadius: 'large',
    preset: 'pink'
  },
  blue: {
    primaryColor: '#3b82f6',
    secondaryColor: '#1e3a8a',
    accentColor: '#06b6d4',
    backgroundColor: '#f0f9ff',
    cardBackground: '#ffffff',
    headerBackground: '#1e3a8a',
    footerBackground: '#1e3a8a',
    textColor: '#1e293b',
    textLight: '#64748b',
    headerText: '#ffffff',
    footerText: '#bfdbfe',
    linkColor: '#3b82f6',
    buttonPrimary: '#3b82f6',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#1e3a8a',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#bfdbfe',
    inputFocus: '#3b82f6',
    borderRadius: 'medium',
    preset: 'blue'
  },
  green: {
    primaryColor: '#10b981',
    secondaryColor: '#064e3b',
    accentColor: '#34d399',
    backgroundColor: '#ecfdf5',
    cardBackground: '#ffffff',
    headerBackground: '#064e3b',
    footerBackground: '#064e3b',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#a7f3d0',
    linkColor: '#10b981',
    buttonPrimary: '#10b981',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#064e3b',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#a7f3d0',
    inputFocus: '#10b981',
    borderRadius: 'medium',
    preset: 'green'
  },
  purple: {
    primaryColor: '#8b5cf6',
    secondaryColor: '#4c1d95',
    accentColor: '#a78bfa',
    backgroundColor: '#f5f3ff',
    cardBackground: '#ffffff',
    headerBackground: '#4c1d95',
    footerBackground: '#4c1d95',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#ddd6fe',
    linkColor: '#8b5cf6',
    buttonPrimary: '#8b5cf6',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#4c1d95',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#ddd6fe',
    inputFocus: '#8b5cf6',
    borderRadius: 'medium',
    preset: 'purple'
  },
  orange: {
    primaryColor: '#f97316',
    secondaryColor: '#9a3412',
    accentColor: '#fb923c',
    backgroundColor: '#fff7ed',
    cardBackground: '#ffffff',
    headerBackground: '#9a3412',
    footerBackground: '#9a3412',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#fed7aa',
    linkColor: '#f97316',
    buttonPrimary: '#f97316',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#9a3412',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#fed7aa',
    inputFocus: '#f97316',
    borderRadius: 'medium',
    preset: 'orange'
  },
  teal: {
    primaryColor: '#14b8a6',
    secondaryColor: '#134e4a',
    accentColor: '#2dd4bf',
    backgroundColor: '#f0fdfa',
    cardBackground: '#ffffff',
    headerBackground: '#134e4a',
    footerBackground: '#134e4a',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#99f6e4',
    linkColor: '#14b8a6',
    buttonPrimary: '#14b8a6',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#134e4a',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#99f6e4',
    inputFocus: '#14b8a6',
    borderRadius: 'medium',
    preset: 'teal'
  },
  gold: {
    primaryColor: '#eab308',
    secondaryColor: '#713f12',
    accentColor: '#fbbf24',
    backgroundColor: '#fefce8',
    cardBackground: '#ffffff',
    headerBackground: '#713f12',
    footerBackground: '#713f12',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#fef08a',
    linkColor: '#ca8a04',
    buttonPrimary: '#eab308',
    buttonPrimaryText: '#1f2937',
    buttonSecondary: '#713f12',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#fef08a',
    inputFocus: '#eab308',
    borderRadius: 'medium',
    preset: 'gold'
  },
  rosegold: {
    primaryColor: '#e11d48',
    secondaryColor: '#4a1c2a',
    accentColor: '#f43f5e',
    backgroundColor: '#fff1f2',
    cardBackground: '#ffffff',
    headerBackground: '#4a1c2a',
    footerBackground: '#4a1c2a',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#fecdd3',
    linkColor: '#e11d48',
    buttonPrimary: '#e11d48',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#4a1c2a',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#fecdd3',
    inputFocus: '#e11d48',
    borderRadius: 'large',
    preset: 'rosegold'
  },
  midnight: {
    primaryColor: '#6366f1',
    secondaryColor: '#1e1b4b',
    accentColor: '#818cf8',
    backgroundColor: '#0f0e1a',
    cardBackground: '#1e1b4b',
    headerBackground: '#1e1b4b',
    footerBackground: '#1e1b4b',
    textColor: '#e2e8f0',
    textLight: '#94a3b8',
    headerText: '#e2e8f0',
    footerText: '#a5b4fc',
    linkColor: '#818cf8',
    buttonPrimary: '#6366f1',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#312e81',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#f87171',
    buttonDangerText: '#1e1b4b',
    inputBackground: '#1e1b4b',
    inputBorder: '#4338ca',
    inputFocus: '#6366f1',
    borderRadius: 'medium',
    preset: 'midnight'
  },
  ocean: {
    primaryColor: '#0891b2',
    secondaryColor: '#164e63',
    accentColor: '#22d3ee',
    backgroundColor: '#ecfeff',
    cardBackground: '#ffffff',
    headerBackground: '#164e63',
    footerBackground: '#164e63',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#a5f3fc',
    linkColor: '#0891b2',
    buttonPrimary: '#0891b2',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#164e63',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#a5f3fc',
    inputFocus: '#0891b2',
    borderRadius: 'medium',
    preset: 'ocean'
  },
  sunset: {
    primaryColor: '#f59e0b',
    secondaryColor: '#7c2d12',
    accentColor: '#ef4444',
    backgroundColor: '#fffbeb',
    cardBackground: '#ffffff',
    headerBackground: '#7c2d12',
    footerBackground: '#7c2d12',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#fcd34d',
    linkColor: '#f59e0b',
    buttonPrimary: '#f59e0b',
    buttonPrimaryText: '#1f2937',
    buttonSecondary: '#7c2d12',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#fcd34d',
    inputFocus: '#f59e0b',
    borderRadius: 'medium',
    preset: 'sunset'
  },
  forest: {
    primaryColor: '#22c55e',
    secondaryColor: '#14532d',
    accentColor: '#4ade80',
    backgroundColor: '#f0fdf4',
    cardBackground: '#ffffff',
    headerBackground: '#14532d',
    footerBackground: '#14532d',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#bbf7d0',
    linkColor: '#22c55e',
    buttonPrimary: '#22c55e',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#14532d',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#bbf7d0',
    inputFocus: '#22c55e',
    borderRadius: 'medium',
    preset: 'forest'
  },
  coral: {
    primaryColor: '#fb7185',
    secondaryColor: '#9f1239',
    accentColor: '#fda4af',
    backgroundColor: '#fff1f2',
    cardBackground: '#ffffff',
    headerBackground: '#9f1239',
    footerBackground: '#9f1239',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#fecdd3',
    linkColor: '#fb7185',
    buttonPrimary: '#fb7185',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#9f1239',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#fecdd3',
    inputFocus: '#fb7185',
    borderRadius: 'large',
    preset: 'coral'
  },
  lavender: {
    primaryColor: '#a855f7',
    secondaryColor: '#581c87',
    accentColor: '#c084fc',
    backgroundColor: '#faf5ff',
    cardBackground: '#ffffff',
    headerBackground: '#581c87',
    footerBackground: '#581c87',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#e9d5ff',
    linkColor: '#a855f7',
    buttonPrimary: '#a855f7',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#581c87',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#e9d5ff',
    inputFocus: '#a855f7',
    borderRadius: 'large',
    preset: 'lavender'
  },
  slate: {
    primaryColor: '#64748b',
    secondaryColor: '#1e293b',
    accentColor: '#94a3b8',
    backgroundColor: '#f8fafc',
    cardBackground: '#ffffff',
    headerBackground: '#1e293b',
    footerBackground: '#1e293b',
    textColor: '#1e293b',
    textLight: '#64748b',
    headerText: '#ffffff',
    footerText: '#cbd5e1',
    linkColor: '#475569',
    buttonPrimary: '#64748b',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#1e293b',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#ef4444',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#cbd5e1',
    inputFocus: '#64748b',
    borderRadius: 'small',
    preset: 'slate'
  },
  cherry: {
    primaryColor: '#dc2626',
    secondaryColor: '#7f1d1d',
    accentColor: '#f87171',
    backgroundColor: '#fef2f2',
    cardBackground: '#ffffff',
    headerBackground: '#7f1d1d',
    footerBackground: '#7f1d1d',
    textColor: '#1f2937',
    textLight: '#6b7280',
    headerText: '#ffffff',
    footerText: '#fecaca',
    linkColor: '#dc2626',
    buttonPrimary: '#dc2626',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: '#7f1d1d',
    buttonSecondaryText: '#ffffff',
    buttonDanger: '#b91c1c',
    buttonDangerText: '#ffffff',
    inputBackground: '#ffffff',
    inputBorder: '#fecaca',
    inputFocus: '#dc2626',
    borderRadius: 'medium',
    preset: 'cherry'
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/theme-settings/public`);
      if (response.ok) {
        const data = await response.json();
        const loadedTheme = { ...defaultTheme, ...data };
        setTheme(loadedTheme);
        applyThemeToDocument(loadedTheme);
      } else {
        // Use default and apply
        applyThemeToDocument(defaultTheme);
      }
    } catch (error) {
      console.error('Failed to load theme:', error);
      applyThemeToDocument(defaultTheme);
    } finally {
      setLoading(false);
    }
  };

  const updateTheme = (newTheme) => {
    setTheme(newTheme);
    applyThemeToDocument(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, loading, updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
